import { useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { components as SelectComponents } from 'react-select';
import type {
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  MultiValueRemoveProps,
  OptionProps,
  Props as SelectProps,
  SelectInstance,
  ThemeConfig
} from 'react-select';
import tokens from '@gjensidige/builders-tokens/tokens-web.json';
import { Close } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';

import { Chevron, Input, Spinner } from '..';
import styles from './dropdown.module.less';
import { useTracking, getLabel } from '../utils';

export type DropdownBaseProps = {
  className?: string;
  components?: SelectProps['components'];
  id?: string;
  inputId?: string;
  invalid?: boolean;
  options?: SelectProps['options'];
  outerId?: string;
  placeholder?: SelectProps['placeholder'];
  selectRef?: React.ForwardedRef<SelectInstance>;
  size?: number | string | 'max-content';
  style?: JSX.IntrinsicElements['div']['style'];
  tracking?: TrackingProp;
  value?: SelectProps['value'];
};

type SimpleRestOptions = { label: string; options: { label: string }[] }[];

let valueOnFocus: string; // Used to check if there is actually a change between focus and blur
const SIZE_VAR = '--builders-dropdown-size';
const SIZE_PAD = '80px'; // The space needed for chevron and padding
export const Render = (
  // eslint-disable-next-line
  Select: any,
  {
    className,
    components = {},
    id,
    inputId,
    invalid,
    placeholder = '',
    outerId,
    selectRef,
    size,
    tracking = true,
    ...rest
  }: DropdownBaseProps
) => {
  const innerRef = useRef<SelectInstance>();
  const track = useTracking(tracking);

  const handleOpen: SelectProps['onMenuOpen'] = (...args) => {
    const onMenuOpen = (rest as SelectProps).onMenuOpen;
    valueOnFocus = getTrackingValue(innerRef.current);
    onMenuOpen?.(...args);
  };

  const handleClose: SelectProps['onMenuClose'] = (...args) => {
    const onMenuClose = (rest as SelectProps).onMenuClose;
    onMenuClose?.(...args);
    if (valueOnFocus !== getTrackingValue(innerRef.current)) {
      track({
        eventCategory: 'dropdown',
        eventAction: 'change',
        eventLabel: getLabel(innerRef.current?.inputRef),
        eventStatus: getTrackingValue(innerRef.current)
      });
    }
  };

  useImperativeHandle(selectRef, () => innerRef.current as SelectInstance); // Forward innerRef

  // If size="max-content" we need to do some calculations
  useEffect(() => {
    const control = innerRef.current?.controlRef;
    const select = control?.closest<HTMLSelectElement>(`.${styles.select}`);

    if (size !== 'max-content' || !rest.options || !select) return;
    const optionsOrGroups = rest.options as SimpleRestOptions;
    const options = optionsOrGroups.flatMap((item) => item.options || item);
    const labels = options.map(({ label }) => label).concat(`${placeholder}`);
    const width = getTextWidth(labels.join('\n'), select);

    select.style.setProperty(SIZE_VAR, `calc(${width}px + ${SIZE_PAD})`);
  }, [size, placeholder, rest.options]);

  // Support value|defaultValue={{ value: '', label: '' }} or just value|defaultValue=''
  const val = (rest.value ? 'value' : 'defaultValue') as keyof typeof rest;
  if (typeof rest[val] === 'string' && Array.isArray(rest.options))
    Object.assign(rest, {
      [val]: rest.options.find(({ value }) => value === rest[val])
    });

  return (
    <Select
      hideSelectedOptions={false}
      loadingMessage={() => <Spinner />}
      noOptionsMessage={() => '\u{2014}'}
      isSearchable={false}
      /* Props above can be overwritten */
      {...rest}
      aria-invalid={invalid ? true : undefined}
      className={clsx(styles.select, className)}
      components={{ ...componentsConfig, ...components }}
      id={outerId}
      inputId={inputId || id} // Accept both inputId and id for supporting react-select props but also being consistent with other components
      onMenuClose={handleClose}
      onMenuOpen={handleOpen}
      placeholder={placeholder}
      ref={innerRef}
      styles={styleConfig(size)}
      theme={theme}
    />
  );
};

const getTextWidth = (text: string, mount: Element) => {
  const span = document.createElement('span');
  span.style.whiteSpace = 'pre'; // Respect newlines
  span.textContent = text;
  const width = mount.appendChild(span).offsetWidth;
  mount.removeChild(span); // Clean up
  return width;
};

const getTrackingValue = (selectRef?: SelectInstance) => {
  const value = selectRef?.getValue() || [];
  const values = Array.prototype.concat.call([], value || []);
  const labels = values.map(({ label }) => label);
  return labels.sort().join('_');
};

// Custom component declared outside of the react-select scope for better performance
const onChange = () => null; // Need a empty onChange to make old react happy
const componentsConfig = {
  ClearIndicator: null,
  IndicatorSeparator: null,
  LoadingIndicator: null,
  DropdownIndicator: (props: DropdownIndicatorProps) => (
    <Chevron className={styles.chevron} open={props.selectProps.menuIsOpen} />
  ),
  MultiValueRemove: (props: MultiValueRemoveProps) => (
    <SelectComponents.MultiValueRemove {...props}>
      <Close width={16} height={16} />
    </SelectComponents.MultiValueRemove>
  ),
  Option: (props: OptionProps) => (
    <SelectComponents.Option {...props}>
      <label className={styles.label}>
        {props.isMulti && (
          <Input
            type="checkbox"
            checked={props.isSelected}
            onChange={onChange}
            tracking={false}
          />
        )}
        <span>{props.label}</span>
      </label>
    </SelectComponents.Option>
  )
};

const styleConfig = (size: DropdownBaseProps['size']) => ({
  control: (
    style: CSSObjectWithLabel,
    state: { menuIsOpen: boolean; selectProps: { 'aria-invalid'?: boolean } }
  ) => ({
    ...style,
    backgroundColor: state.selectProps['aria-invalid']
      ? tokens.color.error100
      : tokens.color.white,
    border: '2px solid',
    borderBottomLeftRadius: state.menuIsOpen ? 0 : null,
    borderBottomRightRadius: state.menuIsOpen ? 0 : null,
    boxShadow: 'none',
    boxSizing: 'border-box',
    color: state.selectProps['aria-invalid'] ? tokens.color.error300 : null,
    cursor: 'pointer',
    minHeight: `calc(2em + ${tokens.unit20})`, // Becomes 56px(md) => 48px(sm) due to font-md going from 20px(md) to 16px(sm)
    outlineOffset: 2,
    padding: '0 3px',
    transition: null,
    maxWidth: '100%',
    width: Number(size)
      ? `calc(${size}ch + ${SIZE_PAD})` // If size is numeric, set a caluclated size and add space for the chevron
      : `var(${SIZE_VAR})`,
    '&:hover': null,
    '&:focus-within': {
      backgroundColor: tokens.color.yellow100,
      outline: state.menuIsOpen
        ? null
        : `3px solid ${tokens.color.purple700}!important` // Ugly but needed since react-select uses !important
    }
  }),
  menu: (style: CSSObjectWithLabel) => ({
    ...style,
    border: '2px solid',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: 'none',
    marginTop: -2,
    width: `var(${SIZE_VAR})`,
    maxWidth: '100%',
    zIndex: 9999 // Set above other content
  }),
  option: (style: CSSObjectWithLabel) => ({
    ...style,
    color: 'inherit',
    cursor: 'pointer',
    padding: 0
  }),
  multiValue: (style: CSSObjectWithLabel) => ({
    ...style,
    backgroundColor: 'none',
    border: '2px solid',
    borderRadius: 99,
    paddingLeft: 8,
    alignItems: 'center',
    fontSize: tokens.font.max.sm,
    fontWeight: tokens.font.weight500
  }),
  multiValueRemove: (style: CSSObjectWithLabel) => ({
    ...style,
    borderRadius: 99,
    padding: 4,
    marginTop: 4,
    marginRight: 4,
    marginBottom: 4,
    '&:hover': {
      backgroundColor: tokens.color.blue200,
      color: tokens.color.blue700
    }
  })
});

const theme: ThemeConfig = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    danger: tokens.color.error200,
    dangerLight: tokens.color.error100,
    neutral0: tokens.color.white,
    neutral5: tokens.color.blue100,
    neutral10: tokens.color.blue100,
    neutral20: tokens.color.blue200,
    neutral30: tokens.color.blue300,
    neutral40: tokens.color.blue400,
    neutral50: tokens.color.blue500,
    neutral60: tokens.color.blue500,
    neutral70: tokens.color.blue600,
    neutral80: tokens.color.blue700,
    neutral90: tokens.color.blue700,
    primary25: tokens.color.blue100,
    primary50: tokens.color.blue200,
    primary75: tokens.color.blue200,
    primary: tokens.color.yellow200
  }
});
